/**
 * Kesh King header: standalone vanilla custom element.
 *
 * Owns the drawer (open, close, nested panels), keyboard and focus handling, scroll lock,
 * and the sticky states. The sticky wrapper sits in flow at the top of the page. Once the page has
 * scrolled past the full header height, the wrapper becomes fixed, and the component keeps its full
 * height as a placeholder so the page does not jump. Loaded as a module from sections/kk-header.liquid.
 * No external dependencies.
 */
const DRAWER_OPEN_CLASS = 'is-open';
const HIDDEN_CLASS = 'kk-is-hidden';
const STUCK_CLASS = 'kk-is-stuck';
const COMPACT_CLASS = 'kk-is-compact';
const ENTERING_CLASS = 'kk-is-entering';
// Within this distance of the top, the header returns to its normal in-flow position.
const TOP_EPSILON = 4;

const FOCUSABLE = 'a[href], button:not([disabled]), select, input:not([disabled])';

class KkHeaderComponent extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.sticky = this.querySelector('[data-kk-sticky]');
    this.header = this.querySelector('[data-kk-header]');
    this.drawer = this.querySelector('[data-kk-drawer]');
    this.overlay = this.querySelector('[data-kk-overlay]');
    this.toggle = this.querySelector('[data-kk-menu-toggle]');
    this.mode = this.dataset.sticky || 'none';
    this.threshold = Number(this.dataset.threshold) || 0;
    this.tolerance = Number(this.dataset.tolerance) || 0;
    this.compactEnabled = this.dataset.compact !== 'false';
    this.lastY = window.scrollY;
    this.direction = 0;
    this.anchorY = 0;
    this.stuck = false;
    this.hidden = false;
    this.fullHeight = 0;
    this.ticking = false;
    this.returnFocus = null;

    if (!this.sticky || !this.header || !this.drawer || !this.toggle) return;

    this.toggle.addEventListener('click', () => this.isDrawerOpen() ? this.closeDrawer() : this.openDrawer(), { signal });
    this.overlay?.addEventListener('click', () => this.closeDrawer(), { signal });
    this.drawer.querySelectorAll('[data-kk-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(), { signal });
    });

    this.querySelectorAll('[data-kk-expand]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.openPanel(trigger), { signal });
    });
    this.querySelectorAll('[data-kk-back]').forEach((back) => {
      back.addEventListener('click', () => this.closePanel(back), { signal });
    });

    // Language disclosure: a button opens the list; choosing a language submits the localization form.
    this.querySelectorAll('[data-kk-lang]').forEach((lang) => {
      const toggle = lang.querySelector('[data-kk-lang-toggle]');
      const list = lang.querySelector('.kk-lang__list');
      if (!toggle || !list) return;
      const setOpen = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        list.hidden = !open;
      };
      toggle.addEventListener('click', () => setOpen(list.hidden), { signal });
      document.addEventListener('click', (event) => {
        if (!lang.contains(event.target)) setOpen(false);
      }, { signal });
      lang.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !list.hidden) {
          setOpen(false);
          toggle.focus();
        }
      }, { signal });
    });

    // Search and cart reuse the theme's own dialog and drawer components.
    this.querySelector('[data-kk-search]')?.addEventListener('click', (event) => {
      const dialog = document.getElementById('search-modal');
      if (typeof dialog?.showDialog === 'function') {
        event.preventDefault();
        dialog.showDialog();
      }
    }, { signal });
    this.querySelector('[data-kk-cart]')?.addEventListener('click', (event) => {
      const drawer = document.getElementById('cart-drawer');
      if (typeof drawer?.toggle === 'function') {
        event.preventDefault();
        drawer.toggle();
      }
    }, { signal });

    // A keyboard user who tabs into a hidden header brings it back.
    this.sticky.addEventListener('focusin', () => this.setHidden(false), { signal });

    this.drawer.addEventListener('keydown', (event) => this.onDrawerKeydown(event), { signal });
    document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal });
    // Horizon scrolls .page-wrapper instead of the window at desktop widths. A capture listener on the
    // document hears scroll events from either scroll container.
    document.addEventListener('scroll', () => this.requestScrollUpdate(), { passive: true, capture: true, signal });

    this.resizeObserver = new ResizeObserver(() => {
      this.measureFullHeight();
      this.syncHeaderHeight();
    });
    this.resizeObserver.observe(this.sticky);

    this.measureFullHeight();
    this.syncHeaderHeight();
    this.updateScrollState();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.unlockScroll();
  }

  isDrawerOpen() {
    return this.drawer.classList.contains(DRAWER_OPEN_CLASS);
  }

  // The full height is the in-flow height of the announcement bar plus the navbar. It is only
  // measured while the header is in flow, so the placeholder never shrinks to the compact height.
  measureFullHeight() {
    if (this.stuck) return;
    const height = Math.round(this.sticky.offsetHeight);
    if (!height) return;
    this.fullHeight = height;
    this.style.minBlockSize = `${height}px`;
  }

  syncHeaderHeight() {
    // The drawer sits directly below the header. Use the header's bottom edge in the viewport.
    const bottom = Math.max(0, Math.round(this.sticky.getBoundingClientRect().bottom));
    this.style.setProperty('--kk-header-h', `${bottom}px`);
  }

  openDrawer() {
    if (this.isDrawerOpen()) return;
    this.returnFocus = document.activeElement;
    this.drawer.classList.add(DRAWER_OPEN_CLASS);
    this.overlay?.classList.add(DRAWER_OPEN_CLASS);
    this.toggle.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.setHidden(false);
    this.syncHeaderHeight();
    this.focusFirst(this.drawer);
  }

  closeDrawer() {
    if (!this.isDrawerOpen()) return;
    // Close nested panels first so the next open starts at the root list.
    this.drawer.querySelectorAll(`[data-kk-panel].${DRAWER_OPEN_CLASS}`).forEach((panel) => this.setPanelOpen(panel, false));
    this.querySelectorAll('[data-kk-expand][aria-expanded="true"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.drawer.classList.remove(DRAWER_OPEN_CLASS);
    this.overlay?.classList.remove(DRAWER_OPEN_CLASS);
    this.toggle.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    const target = this.returnFocus && this.returnFocus.isConnected ? this.returnFocus : this.toggle;
    target.focus({ preventScroll: true });
  }

  openPanel(trigger) {
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    this.setPanelOpen(panel, true);
    this.focusFirst(panel);
  }

  closePanel(back) {
    const panel = back.closest('[data-kk-panel]');
    if (!panel) return;
    const trigger = this.querySelector(`[data-kk-expand][aria-controls="${panel.id}"]`);
    this.setPanelOpen(panel, false);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }
  }

  setPanelOpen(panel, open) {
    panel.classList.toggle(DRAWER_OPEN_CLASS, open);
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  openPanels() {
    return [...this.drawer.querySelectorAll(`[data-kk-panel].${DRAWER_OPEN_CLASS}`)];
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.isDrawerOpen()) return;
    event.preventDefault();
    const panels = this.openPanels();
    if (panels.length) {
      // Close the deepest open panel first.
      const deepest = panels[panels.length - 1];
      this.closePanel(deepest.querySelector('[data-kk-back]'));
      return;
    }
    this.closeDrawer();
  }

  onDrawerKeydown(event) {
    if (event.key !== 'Tab') return;
    const focusable = this.visibleFocusable(this.drawer);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  visibleFocusable(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => {
      if (el.closest('[aria-hidden="true"]')) return false;
      return getComputedStyle(el).visibility !== 'hidden' && el.getClientRects().length > 0;
    });
  }

  focusFirst(root) {
    const first = this.visibleFocusable(root)[0];
    if (first) first.focus({ preventScroll: true });
  }

  lockScroll() {
    if (document.documentElement.dataset.kkScrollLock === 'true') return;
    document.documentElement.dataset.kkScrollLock = 'true';
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (document.documentElement.dataset.kkScrollLock !== 'true') return;
    delete document.documentElement.dataset.kkScrollLock;
    document.documentElement.style.overflow = '';
  }

  // The element that actually scrolls: the nearest scrollable ancestor, otherwise the window.
  scrollPosition() {
    let node = this.parentElement;
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node.scrollTop;
      }
      node = node.parentElement;
    }
    return window.scrollY;
  }

  requestScrollUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.updateScrollState();
    });
  }

  setStuck(stuck, movingDown) {
    if (this.stuck === stuck) return;
    this.stuck = stuck;
    this.anchorY = this.scrollPosition();
    if (stuck) {
      // Enter the fixed state without a transition, so the header never flashes at the top.
      this.sticky.classList.add(ENTERING_CLASS);
      this.sticky.classList.toggle(STUCK_CLASS, true);
      this.setHidden(this.mode !== 'always' && movingDown);
      requestAnimationFrame(() => this.sticky.classList.remove(ENTERING_CLASS));
    } else {
      this.sticky.classList.remove(ENTERING_CLASS, STUCK_CLASS, HIDDEN_CLASS, COMPACT_CLASS);
      this.hidden = false;
      this.measureFullHeight();
    }
    this.syncHeaderHeight();
  }

  setHidden(hidden) {
    if (this.hidden === hidden) return;
    this.hidden = hidden;
    this.sticky.classList.toggle(HIDDEN_CLASS, hidden);
  }

  updateScrollState() {
    const y = this.scrollPosition();
    const dy = y - this.lastY;
    this.lastY = y;

    if (this.mode === 'none') return;

    if (y <= TOP_EPSILON) {
      this.setStuck(false);
      return;
    }
    if (this.mode === 'always') {
      this.setStuck(true, dy > 0);
    } else if (y > this.fullHeight) {
      // Hysteresis: between the top and the full height, keep the current state so it never flickers.
      this.setStuck(true, dy > 0);
    }

    if (!this.stuck) return;

    this.sticky.classList.toggle(COMPACT_CLASS, this.compactEnabled && y > this.threshold);

    if (this.mode === 'always' || this.isDrawerOpen() || y <= this.threshold) {
      this.setHidden(false);
      return;
    }

    // Direction is measured from the last point where the scroll reversed. Movement smaller than the
    // tolerance never changes the header, so jitter and tiny reversals cannot make it flicker.
    if (dy !== 0 && Math.sign(dy) !== this.direction) {
      this.direction = Math.sign(dy);
      this.anchorY = y - dy;
    }
    if (this.direction > 0 && y - this.anchorY > this.tolerance) {
      this.setHidden(true);
    } else if (this.direction < 0 && this.anchorY - y > this.tolerance) {
      this.setHidden(false);
    }
  }
}

if (!customElements.get('kk-header-component')) {
  customElements.define('kk-header-component', KkHeaderComponent);
}
